import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, type JSX } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { optionalAddressBlockPreview, OptionnalAddressBlock } from '@/features/ui';
import { OptionalAddressBlockSchema } from '@/features/ui/OptionalAddressBlock/model';

import { FormTextArea, StepControlPanel, FormDatePicker, Grid } from '@/shared/ui';

import { FormConstants } from '@/components/Forms/constants';

import { MissingPersonStatus } from '../../lib/constants';
import { MissingPersonFormData } from '../../model/types';

export const MissingPersonDisappearanceInfo: React.FC<StepProps> = ({ next, prev }) => {
  const { control, setValue, watch } = useFormContext<MissingPersonFormData>();

  const { missingPersonDisappearanceInfo, missingPerson } = watch();

  useEffect(() => {
    if (isEmpty(missingPersonDisappearanceInfo)) {
      setValue('missingPersonDisappearanceInfo', {
        country: null,
        // @ts-ignore
        apartment: null,
        building: null,
        district: null,
        house: null,
        region: null,
        settlement: null,
        street: null,
        custom_street: null,
        zip_code: null,
        missingPersonStatusId: missingPerson.person_status.id ?? null,
      });
    }
  }, []);

  const disappearanceAddressValue = useMemo<OptionalAddressBlockSchema>(() => {
    const addr = missingPersonDisappearanceInfo || {};
    return {
      country: addr.country || null,
      apartment: null,
      building: null,
      district: addr.district || null,
      house: null,
      region: addr.region || null,
      settlement: addr.settlement || null,
      street: addr.street || null,
      custom_street: addr.custom_street || null,
      zip_code: null,
    };
  }, [missingPersonDisappearanceInfo]);

  return (
    <>
      <FormBlock title="Інформація про зникнення">
        <Controller
          name="missingPersonDisappearanceInfo"
          control={control}
          render={({ field, formState: { errors } }): JSX.Element => (
            <OptionnalAddressBlock
              withCustomStreet
              address={disappearanceAddressValue}
              hideAppartments
              revalidate={!isEmpty(errors)}
              includeZipCode={false}
              handleAddressChange={(newAddress): void => {
                field.onChange({
                  disappearance_date: missingPersonDisappearanceInfo.disappearance_date,
                  disappearance_circumstances: missingPersonDisappearanceInfo.disappearance_circumstances,
                  missingPersonStatusId: missingPersonDisappearanceInfo.missingPersonStatusId,
                  ...newAddress,
                });
              }}
            />
          )}
        />

        <FormDatePicker
          maxDateToday
          name="missingPersonDisappearanceInfo.disappearance_date"
          label="Дата зникнення"
          control={control}
        />

        <FormTextArea
          name="missingPersonDisappearanceInfo.disappearance_circumstances"
          label="Обставини зникнення та інша інформація про місце зникнення"
          required={missingPerson.person_status.id === MissingPersonStatus.civil}
          maxLength={FormConstants.INPUT_MAX_LENGTH_512}
          control={control}
        />
      </FormBlock>

      <StepControlPanel prev={prev} next={next} />
    </>
  );
};

export const MissingPersonDisappearanceInfoPreview: React.FC = () => {
  const { getValues } = useFormContext<MissingPersonFormData>();
  const disappearanceAddress = getValues('missingPersonDisappearanceInfo');

  const disappearanceDate = getValues('missingPersonDisappearanceInfo.disappearance_date');
  const disappearanceCircumstances = getValues('missingPersonDisappearanceInfo.disappearance_circumstances');

  const labelValueListData: Array<[string, Nullable<string>]> = [
    ...optionalAddressBlockPreview({
      address: disappearanceAddress,
      includeZipCode: false,
      hideAppartments: true,
      enableCustomAddressFields: true,
    }),
    ['Дата зникнення', disappearanceDate],
    ['Обставини зникнення та інша інформація про місце зникнення', disappearanceCircumstances],
  ];

  return (
    <>
      <Grid title="Інформація про зникнення" data={labelValueListData} />
    </>
  );
};
